import React, { useState, useCallback, useEffect } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getFields } from '@/services/workflowApi';
import type { IntegrationField, DestinationActionSettings } from '@/types/workflow';
import { useMixpanel } from '@/hooks/useMixpanel';
import { COMPLETED_FIELD_MAPPING } from '@/analytics/mixpanel/constants/events';
import { FIELDS_MAPPED } from '@/analytics/mixpanel/constants/properties';

/** Canonical key for matching: lowercase, non-alphanumeric removed. */
function toCanonicalKey(s: string): string {
  return s.trim().toLowerCase().replace(/\W+/g, '');
}

/** Strip "(required)" / "(optional)" suffix for display matching. */
function stripCommonSuffixes(s: string): string {
  return s.replace(/\s*\((required|optional)\)\s*$/i, '').trim();
}

// ── Row type ──────────────────────────────────────────────────────────────────

interface MappingRow {
  id: string;
  integrationField: IntegrationField | null;
  leadpagesField: string | null;
  /** Required rows are anchored to a specific integration field; optional rows are user-added. */
  isRequired: boolean;
}

// ── Sub-components ────────────────────────────────────────────────────────────

const Spinner: React.FC = () => (
  <div className="flex justify-center py-4">
    <div className="h-5 w-5 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600" />
  </div>
);

const ErrorBox: React.FC<{ message: string }> = ({ message }) => (
  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5" role="alert">
    <p className="text-[11px] leading-relaxed text-red-600">{message}</p>
  </div>
);

const RemoveRowIcon: React.FC = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M2 2l6 6M8 2l-6 6" />
  </svg>
);

// ── Props ─────────────────────────────────────────────────────────────────────

export interface FieldMappingStepProps {
  destinationSettings: DestinationActionSettings;
  sourceIds: string[];
  /** Pre-populated when editing an existing automation; empty for new automations. */
  initialFieldMapping: Record<string, string>;
  onComplete: (fieldMapping: Record<string, string>) => void;
  /** Called on every rows change so the parent can persist the mapping even without Save & Continue. */
  onMappingChange?: (fieldMapping: Record<string, string>) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const FieldMappingStep: React.FC<FieldMappingStepProps> = ({
  destinationSettings,
  sourceIds,
  initialFieldMapping,
  onComplete,
  onMappingChange,
}) => {
  const track = useMixpanel();
  const [destinationFields, setDestinationFields] = useState<IntegrationField[]>([]);
  const [sourceFields, setSourceFields] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<MappingRow[]>([]);

  // Capture initial mapping in a ref so loadFields's useCallback deps don't include
  // the prop object (which would change reference on every render and re-trigger the effect).
  const initialFieldMappingRef = React.useRef(initialFieldMapping);

  // Keep ref in sync when parent passes updated mapping (e.g. after workflow load or when navigating back).
  React.useEffect(() => {
    initialFieldMappingRef.current = initialFieldMapping;
  }, [initialFieldMapping]);

  const optionalRowCounterRef = React.useRef(0);

  const getMappedSourceField = (
    mapping: Record<string, string>,
    field: IntegrationField,
  ): string | null => {
    const fieldCanonical = toCanonicalKey(field.value) || toCanonicalKey(field.label);
    if (!fieldCanonical) return null;
    for (const [formKey, intVal] of Object.entries(mapping)) {
      if (intVal.trim() === field.value.trim() || intVal.trim() === field.label.trim())
        return formKey;
      if (toCanonicalKey(intVal) === fieldCanonical) return formKey;
    }
    return null;
  };

  const normalizeToSourceField = (value: string | null, sourceFields: string[]): string | null => {
    if (!value || !value.trim()) return null;
    const trimmed = value.trim();
    if (sourceFields.includes(trimmed)) return trimmed;
    const stripped = stripCommonSuffixes(trimmed);
    if (stripped && sourceFields.includes(stripped)) return stripped;
    const vKey = toCanonicalKey(stripped || trimmed);
    if (!vKey) return null;
    const exactCanonical = sourceFields.find((s) => toCanonicalKey(s) === vKey);
    if (exactCanonical) return exactCanonical;
    return null;
  };

  // Find integration field by key (from backend mapping). Prefer exact match so "Name" matches
  // the "Name" field and "First Name" the "First Name" field (same as Leadpages Dashboard).
  // Fall back to canonical only for format variants (e.g. "first_name" -> "First Name").
  const findDestinationFieldByKey = (
    key: string,
    destinationFields: IntegrationField[],
  ): IntegrationField | undefined => {
    const k = key.trim();
    if (!k) return undefined;
    const kLower = k.toLowerCase();
    // Exact match first (case-insensitive): backend key must map to the correct field.
    const exact =
      destinationFields.find(
        (f) => f.value.trim().toLowerCase() === kLower || f.label.trim().toLowerCase() === kLower,
      ) ?? undefined;
    if (exact) return exact;
    // Canonical fallback for format variants (snake_case, etc.).
    const keyCanonical = toCanonicalKey(key);
    if (!keyCanonical) return undefined;
    return destinationFields.find(
      (f) => toCanonicalKey(f.value) === keyCanonical || toCanonicalKey(f.label) === keyCanonical,
    );
  };

  // ── Loader ──────────────────────────────────────────────────────────────────

  // Extract stable primitives so loadFields only re-fires when the API-relevant
  // values change, not when destinationSettings.fieldMapping changes.
  const { credentialId, provider } = destinationSettings;
  // WHY: Different integrations use different config key names for the list/audience ID.
  // Mailchimp uses "list"; others (Kit, MailerLite) use "listId". Check both.
  const configListId =
    typeof destinationSettings.config.listId === 'string'
      ? destinationSettings.config.listId
      : typeof destinationSettings.config.list === 'string'
        ? destinationSettings.config.list
        : undefined;

  const loadFields = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // listId is the most common config key for list-based integrations (Mailchimp, Kit,
      // MailerLite). Only pass it if it's a plain string — array values mean multi-select was
      // used for a different field, and the backend handles the param as optional.
      const response = await getFields(provider, credentialId, sourceIds, configListId);

      if (response._status.code >= 400) throw new Error(`${response._status.code}`);
      if (response.destinationFields.length === 0 || response.sourceFields.length === 0) {
        throw new Error('no_fields');
      }

      // Read mapping after await so we use the latest (e.g. if parent passed it after first paint).
      const mapping = { ...initialFieldMappingRef.current };
      const sourceFieldsList = response.sourceFields;
      const requiredFields = response.destinationFields.filter((f) => f.required);

      const requiredRows: MappingRow[] = requiredFields.map((f) => {
        const raw = getMappedSourceField(mapping, f);
        return {
          id: f.value,
          integrationField: f,
          leadpagesField: normalizeToSourceField(raw, sourceFieldsList),
          isRequired: true,
        };
      });

      const optionalRows: MappingRow[] = Object.entries(mapping)
        .filter(([formKey, intFieldValue]) => {
          if (!formKey?.trim()) return false;
          const matched = findDestinationFieldByKey(intFieldValue, response.destinationFields);
          if (matched) {
            const isRequired = requiredFields.some(
              (f) => f.value === matched.value || f.label === matched.label,
            );
            if (isRequired) return false;
          }
          return true;
        })
        .map(([formFieldKey, intFieldValue]) => {
          const intField = findDestinationFieldByKey(intFieldValue, response.destinationFields);
          const integrationField: IntegrationField = intField ?? {
            value: intFieldValue,
            label: intFieldValue,
            required: false,
          };
          const rowId = intField
            ? `optional-${intField.value}`
            : `optional-saved-${String(intFieldValue).replace(/\s+/g, '_')}`;
          return {
            id: rowId,
            integrationField,
            leadpagesField: normalizeToSourceField(formFieldKey, sourceFieldsList),
            isRequired: false,
          };
        });

      setDestinationFields(response.destinationFields);
      setSourceFields(sourceFieldsList);
      setRows([...requiredRows, ...optionalRows]);
    } catch {
      setError('Failed to load field mapping options. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [credentialId, provider, configListId, sourceIds]);

  useEffect(() => {
    void loadFields();
  }, [loadFields]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const setLeadpagesField = (rowId: string, value: string) => {
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, leadpagesField: value } : r)));
  };

  const setIntegrationField = (rowId: string, value: string) => {
    const field = destinationFields.find((f) => f.value === value) ?? null;
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, integrationField: field } : r)));
  };

  const removeRow = (rowId: string) => {
    setRows((prev) => prev.filter((r) => r.id !== rowId));
  };

  const addRow = () => {
    const id = `optional-new-${++optionalRowCounterRef.current}`;
    setRows((prev) => [
      ...prev,
      { id, integrationField: null, leadpagesField: null, isRequired: false },
    ]);
  };

  // Auto-save mapping to parent on every rows change so navigating back without
  // clicking Save & Continue still preserves what the user has mapped so far.
  useEffect(() => {
    if (!onMappingChange || rows.length === 0) return;
    const fieldMapping: Record<string, string> = {};
    for (const row of rows) {
      if (row.integrationField && row.leadpagesField) {
        fieldMapping[row.leadpagesField] = row.integrationField.value;
      }
    }
    onMappingChange(fieldMapping);
  }, [rows, onMappingChange]);

  const handleContinue = () => {
    const fieldMapping: Record<string, string> = {};
    for (const row of rows) {
      if (row.integrationField && row.leadpagesField) {
        fieldMapping[row.leadpagesField] = row.integrationField.value;
      }
    }
    track(COMPLETED_FIELD_MAPPING, { [FIELDS_MAPPED]: Object.keys(fieldMapping).length });
    onComplete(fieldMapping);
  };

  // ── Derived state ──────────────────────────────────────────────────────────

  const isComplete = rows.filter((r) => r.isRequired).every((r) => r.leadpagesField !== null);

  // Integration display name: strip "custom." prefix (e.g. "custom.brevo" → "Brevo") for column header.
  const integrationDisplayName = destinationSettings.provider.startsWith('custom.')
    ? destinationSettings.provider.slice('custom.'.length)
    : destinationSettings.provider;

  const usedLeadpagesFields = new Set(
    rows.map((r) => r.leadpagesField).filter((f): f is string => f !== null),
  );

  // Integration fields: no restriction — multiple Leadpages fields can map to the same integration field (per AC).
  const availableIntFields = destinationFields;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      className="flex w-full min-w-0 flex-col gap-4 overflow-x-hidden"
      data-testid="field-mapping-step"
    >
      {/* Section header */}
      <div>
        <p className="mb-1 text-[10.5px] font-bold tracking-[0.06em] text-[#9898A2] uppercase">
          Field mapping
        </p>
        <p className="text-xs leading-relaxed text-neutral-500">
          Match your form fields to your integration fields.
        </p>
      </div>

      {loading ? (
        <Spinner />
      ) : error ? (
        <ErrorBox message={error} />
      ) : (
        <>
          {/* Column headers */}
          <div className="flex items-center gap-2">
            <p className="flex-1 text-[10px] font-semibold tracking-wider text-neutral-400 uppercase">
              Form Fields
            </p>
            <span className="w-[14px] shrink-0" />
            <p className="flex-1 text-[10px] font-semibold tracking-wider text-neutral-400 uppercase">
              {integrationDisplayName.charAt(0).toUpperCase() + integrationDisplayName.slice(1)}{' '}
              Fields
            </p>
            <span className="w-[20px] shrink-0" />
          </div>

          {/* Mapping rows */}
          <div className="flex flex-col gap-2" data-testid="mapping-rows">
            {rows.map((row) => {
              const availableLpFields = sourceFields.filter(
                (f) => !usedLeadpagesFields.has(f) || f === row.leadpagesField,
              );
              // Radix Select shows empty if value doesn't match a SelectItem; use only if it exists in options.
              const formFieldSelectValue =
                row.leadpagesField && availableLpFields.includes(row.leadpagesField)
                  ? row.leadpagesField
                  : '';

              return (
                <div key={row.id} className="flex items-center gap-2" data-testid={`row-${row.id}`}>
                  {/* Left: Leadpages field dropdown */}
                  <div className="mx-px min-w-0 flex-1">
                    <Select
                      value={formFieldSelectValue}
                      onValueChange={(v) => setLeadpagesField(row.id, v)}
                    >
                      <SelectTrigger
                        className="w-full"
                        style={{ fontSize: '11px', paddingRight: '26px' }}
                        data-testid={`lp-field-select-${row.id}`}
                      >
                        <SelectValue placeholder="Select" style={{ whiteSpace: 'nowrap' }} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableLpFields.map((f) => (
                          <SelectItem key={f} value={f} truncate={false}>
                            {f}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Arrow connector */}
                  <span className="shrink-0 text-[11px] text-neutral-400" aria-hidden="true">
                    →
                  </span>

                  <div className="mx-px flex min-w-0 flex-1 items-center">
                    {row.isRequired ? (
                      <p
                        className="truncate text-[11px] font-medium text-neutral-700"
                        title={row.integrationField?.label}
                        data-testid={`int-field-label-${row.id}`}
                      >
                        {row.integrationField?.label}
                        <span className="ml-0.5 text-red-500">*</span>
                      </p>
                    ) : row.integrationField === null ||
                      destinationFields.some((f) => f.value === row.integrationField?.value) ? (
                      <Select
                        value={row.integrationField?.value ?? ''}
                        onValueChange={(v) => setIntegrationField(row.id, v)}
                      >
                        <SelectTrigger
                          className="w-full"
                          style={{ fontSize: '11px', paddingRight: '26px' }}
                          data-testid={`int-field-select-${row.id}`}
                        >
                          <SelectValue placeholder="Select" style={{ whiteSpace: 'nowrap' }} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableIntFields.map((f) => (
                            <SelectItem key={f.value} value={f.value} truncate={false}>
                              {f.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p
                        className="truncate text-[11px] font-medium text-neutral-700"
                        title={row.integrationField?.label}
                        data-testid={`int-field-label-${row.id}`}
                      >
                        {row.integrationField?.label}
                      </p>
                    )}
                  </div>

                  {/* Remove button (optional rows only); spacer for required rows */}
                  {row.isRequired ? (
                    <span className="w-[20px] shrink-0" />
                  ) : (
                    <button
                      onClick={() => removeRow(row.id)}
                      className="shrink-0 rounded p-1 text-neutral-400 transition-colors hover:text-neutral-600"
                      aria-label="Remove row"
                      data-testid={`remove-row-${row.id}`}
                    >
                      <RemoveRowIcon />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Add more button */}
          <button
            onClick={addRow}
            className="self-start text-[12px] text-violet-600 transition-colors hover:text-violet-700"
            data-testid="add-mapping-row"
          >
            + Add more
          </button>

          {/* Save & Continue */}
          <button
            onClick={handleContinue}
            disabled={!isComplete}
            data-testid="field-mapping-continue"
            className="flex w-full cursor-pointer items-center justify-center rounded-[8px] bg-violet-600 px-4 py-[9px] text-[13px] font-medium text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save & Continue
          </button>
        </>
      )}
    </div>
  );
};
